/**
 * Nowill-skärmarna — Williams 15 valda byggen längs en BANA genom dynerna.
 *
 * Banan: slingrande rutt mot solen (−z), ~40 m mellan skärmarna, varannan
 * vänster/höger — man glider/surfar från skärm till skärm som en resa.
 *
 * LAZY-AKTIVERING (Williams krav: dra inte data i onödan):
 *   – posters laddas först när spelaren är inom AKTIVERA-radien och släpps
 *     (dispose) bortom SLAPP-radien — långt borta är skärmen en mörk platta
 *   – iframen laddar bara när panelen öppnas och töms när den stängs
 *
 * Panel-mönstret: PLATT DOM-iframe (CSS3D-i-perspektiv är dödfött för klick).
 * Materialet: handskriven WGSL (StandardMaterials texturer binder inte i den
 * här specialriggade pipelinen).
 */
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3 } from "@babylonjs/core/Maths/math";
import { ShaderMaterial } from "@babylonjs/core/Materials/shaderMaterial";
import { ShaderLanguage } from "@babylonjs/core/Materials/shaderLanguage";
import { ShaderStore } from "@babylonjs/core/Engines/shaderStore";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { RawTexture } from "@babylonjs/core/Materials/Textures/rawTexture";
import { Engine } from "@babylonjs/core/Engines/engine";

ShaderStore.ShadersStoreWGSL["nowillSkarmVertexShader"] = `
attribute position: vec3f;
attribute uv: vec2f;
uniform worldViewProjection: mat4x4f;
varying vUv: vec2f;
@vertex
fn main(input: VertexInputs) -> FragmentInputs {
    vertexOutputs.vUv = vertexInputs.uv;
    vertexOutputs.position = uniforms.worldViewProjection * vec4f(vertexInputs.position, 1.0);
}
`;
ShaderStore.ShadersStoreWGSL["nowillSkarmPixelShader"] = `
uniform ljus: f32;
varying vUv: vec2f;
var poster: texture_2d<f32>;
var posterSampler: sampler;
@fragment
fn main(input: FragmentInputs) -> FragmentOutputs {
    let uv = vec2f(fragmentInputs.vUv.x, 1.0 - fragmentInputs.vUv.y);
    let c = textureSample(poster, posterSampler, uv);
    fragmentOutputs.color = vec4f(c.rgb * uniforms.ljus, 1.0);
}
`;

/**
 * Williams slutgiltiga 12 (banan-tavlan :5690/banan, sparat 2026-08-04).
 * Ordningen = banans ordning. Bortvalda på vägen: landing (8/11 tjänster),
 * velocity + observatoriet (Williams val).
 */
export const BYGGEN = [
    { slug: "kupolen", namn: "Kupolen", rad: "Flersidig premium-sajt med scrollfilm", url: "https://nowill-kupolen.pages.dev/" },
    { slug: "tryckeriet", namn: "Tryckeriet", rad: "Nowill som broadsheet — tidningspapper och tryckfärg", url: "https://nowill-tryckeriet.pages.dev/" },
    { slug: "objektet", namn: "Objektet", rad: "Ett enda 3D-objekt bär hela sidan", url: "https://nowill-objektet.pages.dev/" },
    { slug: "fyren", namn: "Fyren", rad: "Nattklättring genom fyrtornet — ett rum per tjänst", url: "https://nowill-fyren.pages.dev/" },
    { slug: "sovereign", namn: "Sovereign", rad: "Grön låg-poly-värld i nio beats", url: "https://nowill-sovereign.pages.dev/" },
    { slug: "orbit", namn: "Orbit", rad: "Omloppsbana → station — katalogen som HUD-resa", url: "https://nowill-orbit.pages.dev/" },
    { slug: "greenhouse", namn: "Greenhouse", rad: "Växthuset — allt växer under samma glas", url: "https://nowill-greenhouse.pages.dev/" },
    { slug: "beacon", namn: "Beacon", rad: "Signalen som leder hem", url: "https://nowill-beacon.pages.dev/" },
    { slug: "campus", namn: "Campus", rad: "Grundad futurism på campus", url: "https://nowill-campus.pages.dev/" },
    { slug: "institute", namn: "Institute", rad: "Tio hallar, en per tjänst", url: "https://nowill-institute.pages.dev/" },
    { slug: "fjord", namn: "Fjord", rad: "Gryningsfjord → verkstad → topp", url: "https://nowill-fjord.pages.dev/" },
    { slug: "registret", namn: "Registret", rad: "Allt vi håller i era händer", url: "https://nowill-registret.pages.dev/" },
];

const OPPNA_DIST = 9.5;    // närhet som öppnar panelen
const OMLADD_DIST = 13;    // gå så långt innan samma skärm öppnar igen
const MAX_FART = 0.9;
const AKTIVERA = 70;       // poster laddas inom denna radie …
const SLAPP = 100;         // … och släpps bortom denna (hysteres)
const HINT_DIST = 26;

const CSS = `
#nw-skarmhint {
    position: fixed; left: 50%; bottom: 64px; transform: translateX(-50%);
    font: 500 14px/1.4 system-ui, sans-serif; color: #ece7db;
    background: rgba(23,21,18,0.55); padding: 8px 18px; border-radius: 999px;
    pointer-events: none; opacity: 0; transition: opacity 0.25s; z-index: 40;
    text-align: center; backdrop-filter: blur(4px);
}
#nw-panel {
    position: fixed; inset: 0; z-index: 60; display: none;
    align-items: center; justify-content: center; padding: 3vh 3vw;
    /* OPAK bakgrund, INGEN backdrop-filter: blur tvingar kompositören att rita om
       hela ytan varje frame — det stal ram-tid från sajtens scroll-scrub-videor. */
    background: #14120f;
}
#nw-panel.oppen { display: flex; }
#nw-panel .ram {
    width: 100%; height: 100%; max-width: 1240px; display: flex;
    flex-direction: column; border-radius: 14px; overflow: hidden;
    background: #17150f; border: 1px solid rgba(236,231,219,0.18);
    box-shadow: 0 30px 80px rgba(0,0,0,0.5);
}
#nw-panel header {
    display: flex; align-items: center; justify-content: space-between;
    gap: 16px; padding: 10px 18px; color: #ece7db;
    font: 500 14px/1.3 system-ui, sans-serif;
    border-bottom: 1px solid rgba(236,231,219,0.14);
}
#nw-panel header .rad { opacity: 0.6; font-weight: 400; }
#nw-panel header .knappar { display: flex; gap: 10px; flex-shrink: 0; }
#nw-panel header a, #nw-panel header button {
    font: 500 13px/1 system-ui, sans-serif; color: #ece7db;
    background: none; border: 1px solid rgba(236,231,219,0.35);
    border-radius: 999px; padding: 8px 16px; cursor: pointer;
    text-decoration: none;
}
#nw-panel header button:hover, #nw-panel header a:hover { background: rgba(236,231,219,0.12); }
/* eget kompositlager åt sajten så den inte målas om med panelen omkring */
#nw-panel iframe { flex: 1; width: 100%; border: 0; background: #fff; contain: strict; }
`;

export class Skarmar {
    /**
     * @param {import("@babylonjs/core/scene").Scene} scene
     * @param {{ heightAt(x:number, z:number): number }} terrain
     * @param {{ position: { x:number, y:number, z:number }, speed: number }} character
     */
    constructor(scene, terrain, character) {
        this.scene = scene;
        this.character = character;
        this.skarmar = [];
        this.oppenIdx = -1;
        this.sparradIdx = -1;

        // mörk platshållar-textur (1×1) för overksamma skärmar
        this.platshallare = new RawTexture(
            new Uint8Array([26, 23, 19, 255]), 1, 1,
            Engine.TEXTUREFORMAT_RGBA, scene, false, false
        );

        // ---------------------------------------------------------- BANAN
        // Slalom-bana (Williams "mer från sida till sida"): varannan skärm långt
        // ut åt vänster, varannan långt ut åt höger — man SVÄNGER genom banan i
        // stället för att gå rakt. ~40 m framåt per skärm, ±105 m i sidled.
        // (PLAY_RADIUS = 620 m klampar spelaren — sista skärmen ligger ~600 m ut.)
        let fx = 0, fz = 8; // "föregående" punkt = spawn
        for (let i = 0; i < BYGGEN.length; i++) {
            const z = -(34 + i * 40);
            const sida = i % 2 === 0 ? 1 : -1;
            const x = sida * (78 + Math.sin(i * 1.7) * 27);
            const y = terrain.heightAt(x, z) + 2.6;

            // vänd skärmen mot punkten man kommer ifrån
            const riktn = Math.atan2(fx - x, fz - z);

            const duk = MeshBuilder.CreatePlane(
                "nw-skarm-" + BYGGEN[i].slug, { width: 7.2, height: 4.5 }, scene
            );
            duk.position.set(x, y, z);
            duk.rotation.y = riktn;
            duk.renderingGroupId = 1;

            const m = new ShaderMaterial(
                "nw-skarmmat-" + i, scene,
                { vertex: "nowillSkarm", fragment: "nowillSkarm" },
                {
                    attributes: ["position", "uv"],
                    uniforms: ["worldViewProjection", "ljus"],
                    samplers: ["poster"],
                    shaderLanguage: ShaderLanguage.WGSL,
                }
            );
            m.setTexture("poster", this.platshallare);
            m.setFloat("ljus", 1.15);
            m.backFaceCulling = false;
            duk.material = m;

            // mörk ram bakom
            const bx = Math.sin(riktn + Math.PI), bz = Math.cos(riktn + Math.PI);
            const ram = MeshBuilder.CreatePlane("nw-ram-" + i, { width: 7.7, height: 5.0 }, scene);
            ram.position.set(x + bx * 0.07, y, z + bz * 0.07);
            ram.rotation.y = riktn;
            ram.renderingGroupId = 1;
            const rm = new StandardMaterial("nw-rammat-" + i, scene);
            rm.disableLighting = true;
            rm.emissiveColor = new Color3(0.055, 0.05, 0.042);
            rm.backFaceCulling = false;
            ram.material = rm;

            this.skarmar.push({ bygge: BYGGEN[i], x, y, z, m, tex: null, laddar: false });
            fx = x; fz = z;
        }

        globalThis.__nwSkarmar = this; // QA-krok

        // ---------------------------------------------------------------- DOM
        const stil = document.createElement("style");
        stil.textContent = CSS;
        document.head.appendChild(stil);

        this.hint = document.createElement("div");
        this.hint.id = "nw-skarmhint";
        document.body.appendChild(this.hint);

        this.panel = document.createElement("div");
        this.panel.id = "nw-panel";
        this.panel.innerHTML = `
            <div class="ram">
                <header>
                    <div><span class="titel"></span> &nbsp;<span class="rad"></span></div>
                    <div class="knappar">
                        <a class="nyflik" target="_blank" rel="noopener">Öppna i ny flik ↗</a>
                        <button class="stang">Tillbaka till sanden (Esc)</button>
                    </div>
                </header>
                <iframe title="bygge"></iframe>
            </div>`;
        document.body.appendChild(this.panel);
        this.panel.querySelector(".stang").addEventListener("click", () => this.stang());
        window.addEventListener("keydown", (e) => {
            if (e.code === "Escape" && this.oppenIdx >= 0) this.stang();
        });
    }

    /** Ladda postern lat; släpp den igen när spelaren dragit vidare. */
    _aktivera(s, d) {
        if (d < AKTIVERA && !s.tex && !s.laddar) {
            s.laddar = true;
            const t = new Texture(
                "/skarmar/" + s.bygge.slug + ".jpg", this.scene, true, false,
                Texture.BILINEAR_SAMPLINGMODE,
                () => { s.tex = t; s.laddar = false; s.m.setTexture("poster", t); },
                () => { s.laddar = false; }
            );
        } else if (d > SLAPP && s.tex) {
            s.m.setTexture("poster", this.platshallare);
            s.tex.dispose();
            s.tex = null;
        }
    }

    /** main.js registrerar hur spelet pausas/återstartas medan panelen är öppen. */
    setPaus(cb) { this._paus = cb; }

    oppna(idx) {
        const s = this.skarmar[idx];
        if (!s.bygge.url) return;
        this.oppenIdx = idx;
        this.panel.querySelector(".titel").textContent = s.bygge.namn;
        this.panel.querySelector(".rad").textContent = s.bygge.rad + " — live, scrolla och klicka fritt";
        this.panel.querySelector(".nyflik").href = s.bygge.url;
        this.panel.querySelector("iframe").src = s.bygge.url;
        this.panel.classList.add("oppen");
        this.hint.style.opacity = "0";
        document.exitPointerLock?.();
        this._paus?.(true);   // frys spelet — sajten får hela maskinen
    }

    stang() {
        if (this.oppenIdx < 0) return;
        this.sparradIdx = this.oppenIdx;
        this.oppenIdx = -1;
        this.panel.classList.remove("oppen");
        this.panel.querySelector("iframe").src = "about:blank";
        this._paus?.(false);  // spelet igång igen
    }

    update() {
        if (this.oppenIdx >= 0) return;
        const p = this.character.position;
        let bast = -1, bastD = 1e9;
        for (let i = 0; i < this.skarmar.length; i++) {
            const s = this.skarmar[i];
            const dx = s.x - p.x, dz = s.z - p.z;
            const d = Math.hypot(dx, dz);
            this._aktivera(s, d);
            if (d < bastD) { bastD = d; bast = i; }
        }
        if (this.sparradIdx >= 0) {
            const s = this.skarmar[this.sparradIdx];
            if (Math.hypot(s.x - p.x, s.z - p.z) > OMLADD_DIST) this.sparradIdx = -1;
        }
        if (bast >= 0 && bastD < HINT_DIST) {
            const s = this.skarmar[bast];
            const nr = `${bast + 1}/${this.skarmar.length}`;
            this.hint.textContent =
                bastD < OPPNA_DIST
                    ? `${nr} · ${s.bygge.namn} — stå stilla så öppnas sajten`
                    : `${nr} · ${s.bygge.namn} · ${s.bygge.rad}`;
            this.hint.style.opacity = "1";
        } else {
            this.hint.style.opacity = "0";
        }
        if (
            bast >= 0 && bast !== this.sparradIdx &&
            bastD < OPPNA_DIST && this.character.speed < MAX_FART
        ) {
            this.oppna(bast);
        }
    }
}
